// PURPOSE:
// Central place to enrich PubMed results with:
//  1) relevance to the user's search terms (default sort)
//  2) relevance to our curated keyword buckets (economic, tx patterns, etc.)
//  3) Keyword hit lists per bucket for UI chips + CSV export
//
// Not sure what I'm doing so I'm putting it together with some things to improve the CSV export

import type {
    KeywordCategoryKey,
    KeywordHits,
    PubMedArticle,
    PubMedSearchParams,
    MatchedKeywordFamily,
    PubMedMatchField,
} from '../types'
import { CORE_KEYWORD_FAMILIES, DISEASE_SPECIFIC_KEYWORD_FAMILIES } from '../pubmedKeywordFamilies'
import { pubmedIndicationConfig } from '../pubmedIndicationConfig'

import type { PubMedKeywordFamily } from '../pubmedKeywordFamilies'

import {
    PUBMED_GEOGRAPHY_BY_ID,
    PUBMED_GEOGRAPHY_ENTITIES,
    PUBMED_GEOGRAPHY_FIELD_CONFIDENCE,
    buildGeographyRollups,
    getActivePubMedGeographySources,
    getContextualDemonymPatterns,
    getExplicitGeographyPatterns,
    getGeographySourcePatterns,
} from '../pubmedGeographyConfig'

import type {
    GeographyIndicationId,
    PubMedGeographyEnrichment,
    PubMedGeographyMatch,
    PubMedGeographyTextField,
} from '../pubmedGeographyConfig'

export type SortMode = 'searchTerms' | 'customKeywords'

export type EnrichedPubMedArticle = PubMedArticle &
    PubMedGeographyEnrichment & {
        searchTermsScore: number
        customKeywordsScore: number

        /**
         * Backward-compatible category summary currently consumed by ArticleCard
         * and existing export logic.
         *
         * This is now derived from matchedKeywordFamilies.
         */
        keywordHits: KeywordHits

        /**
         * Structured keyword matches supporting:
         *
         * category -> measure -> threshold
         *
         * This becomes the primary enrichment result for future filtering,
         * highlighting, exports, and endpoint-library integration.
         */
        matchedKeywordFamilies: MatchedKeywordFamily[]

        _pubmedRank: number
        interventionMatches: string[]
        outcomeMatches: string[]
    }

/**
 * Keyword dictionaries
 */
// I dont need these keywords now that they are using Keyword families
/*const CUSTOM_KEYWORDS: Record<KeywordCategoryKey, string[]> = {
    epidemiology: [
        'epidemiology',
        'prevalence',
        'incidence',
        'burden of disease',
        'mortality',
        'morbidity',
        'risk factor',
        'comorbidity',
        'comorbidities',
        'natural history of disease',
        'demographic characteristics',
    ],
    economic: [
        'cost-effectiveness',
        'cost effectiveness',
        'cost utility',
        'cost-utility',
        'budget impact',
        'healthcare cost',
        'health care cost',
        'economic burden',
        'resource utilization',
        'resource use',
        'hospitalization',
        'hospitalisation',
        'inpatient admissions',
        'ER visits',
        'emergency room visits',
        'payer',
        'payor',
        'reimbursement',
        'icer',
        'incremental cost effectiveness ratio',
        'cost per quality adjusted life year',
        'cost per quality-adjusted life-year',
        'incremental cost',
        'productivity',
        'presenteeism',
        'absenteeism',
    ],
    treatmentPatterns: [
        'treatment pattern',
        'adherence',
        'nonadherence',
        'medication possession ratio',
        'proportion of days covered',
        'compliance',
        'persistence',
        'discontinuation',
        'treatment switch',
        'switching',
        'lines of therapy',
        'line of therapy',
        '1st line',
        '2nd line',
        'dose escalation',
        'rescue medication',
        'treatment failure',
        'therapy failure',
        'refractory',
        'dosing frequency',
    ],
    clinicalEfficacy: [
        'effectiveness',
        'efficacy',
        'treatment response',
        'response rate',
        'disease remission',
        'disease progression',
        'hazard ratio',
        'overall survival',
        'progression free survival',
        'progression-free survival',
        // Eventually we'll want a list of disease dependent efficacy keywords
    ],
    humanistic: [
        'quality of life',
        'quality-of-life',
        'qol',
        'health-related quality of life',
        'health related quality of life',
        'hrqol',
        'eq-5d',
        'sf-36',
        'sf-12',
        'disability adjusted life year',
        'daly',
        'utility',
        'caregiver burden',
        'treatment burden',
        'treatment satisfaction',
        'pill burden',
        // Eventually want a list of disease dependent humanistic keywords
    ],
    safety: [
        'safety',
        'adverse event',
        'serious adverse event',
        'treatment-emergent adverse event',
        'treatment emergent adverse event',
        'TEAEs',
        'tolerability',
        'toxicity',
        'side effect',
        'discontinuation due to treatment',
        'discontinuation due to adverse events',
        'discontinuation due to AEs',
    ],
    competitors: [] /!* Remove until we can build this out to be roadmap dependent ? *!/,
}*/

// Weights for CUSTOM keyword scoring (Score #2)
const CUSTOM_WEIGHTS: Record<KeywordCategoryKey, number> = {
    epidemiology: 1,
    economic: 1,
    treatmentPatterns: 1,
    clinicalEfficacy: 1,
    humanistic: 1,
    competitors: 1,
    safety: 1,
}

// Caps prevent one category from dominating due to repetition
const CUSTOM_MATCH_CAP_PER_CATEGORY = 6

// Bonus for "breadth" (hitting multiple categories)
function breadthBonus(numCategoriesHit: number): number {
    if (numCategoriesHit >= 4) return 6
    if (numCategoriesHit === 3) return 4
    if (numCategoriesHit === 2) return 2
    return 0
}

// -----------------------------
// Text helpers
// -----------------------------
function norm(s: string): string {
    return (s || '').toLowerCase()
}

function normalizeIndicationForKeywordFamilies(value: string | undefined): string {
    return (value || '')
        .replace(/\s*\([A-Za-z0-9\-]+\)\s*$/, '')
        .trim()
        .toLowerCase()
}

function splitSearchInput(value: string | undefined): string[] {
    return (value || '')
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean)
}

function inferIndicationIdFromParams(params: PubMedSearchParams): string | undefined {
    const diseaseInputs = splitSearchInput(params.diseaseArea).map(normalizeIndicationForKeywordFamilies)

    if (!diseaseInputs.length) return undefined

    const match = pubmedIndicationConfig.indications.find((indication) => {
        const candidates = [
            indication.id,
            indication.label,
            ...(indication.aliases || []),
            ...(indication.defaultDiseaseTerms || []),
        ].map(normalizeIndicationForKeywordFamilies)

        return diseaseInputs.some((input) => candidates.includes(input))
    })

    return match?.id
}

function getActiveKeywordFamilies(params: PubMedSearchParams): PubMedKeywordFamily[] {
    const indicationId = inferIndicationIdFromParams(params)

    return [...CORE_KEYWORD_FAMILIES, ...(indicationId ? DISEASE_SPECIFIC_KEYWORD_FAMILIES[indicationId] || [] : [])]
}

function testRegex(pattern: RegExp, text: string): boolean {
    if (!(pattern instanceof RegExp)) {
        return false
    }

    pattern.lastIndex = 0
    const matched = pattern.test(text)
    pattern.lastIndex = 0

    return matched
}

function getRegexMatches(pattern: RegExp, text: string): string[] {
    if (!(pattern instanceof RegExp) || !text) {
        return []
    }

    /**
     * Recreate the regex with the global flag so every matching phrase can be
     * captured without modifying the configured regex instance.
     */
    const flags = Array.from(new Set(`${pattern.flags}g`.split(''))).join('')
    const globalPattern = new RegExp(pattern.source, flags)

    const matches: string[] = []
    let result: RegExpExecArray | null

    while ((result = globalPattern.exec(text)) !== null) {
        const matchedText = result[0]?.trim()

        if (matchedText) {
            matches.push(matchedText)
        }

        /**
         * Protect against zero-length regex matches causing an infinite loop.
         */
        if (result.index === globalPattern.lastIndex) {
            globalPattern.lastIndex += 1
        }
    }

    return Array.from(new Set(matches))
}

function findKeywordFamilyMatches(text: string, families: PubMedKeywordFamily[]): PubMedKeywordFamily[] {
    return families.filter((family) =>
        family.patterns.some((pattern) => {
            if (!(pattern instanceof RegExp)) {
                console.warn(`[PubMed] Ignoring invalid regex pattern in keyword family "${family.id}".`, pattern)
                return false
            }

            return testRegex(pattern, text)
        })
    )
}

function labelsFromFamilies(families: PubMedKeywordFamily[]): string[] {
    return Array.from(new Set(families.map((family) => family.label)))
}

function buildArticleMatchFields(article: PubMedArticle): Array<{ field: PubMedMatchField; text: string }> {
    const fields: Array<{ field: PubMedMatchField; text: string }> = []

    if (article.title) {
        fields.push({
            field: 'title',
            text: article.title,
        })
    }

    if (article.abstract) {
        fields.push({
            field: 'abstract',
            text: article.abstract,
        })
    }

    if (Array.isArray(article.industryAffiliations) && article.industryAffiliations.length > 0) {
        fields.push({
            field: 'industryAffiliations',
            text: article.industryAffiliations.join(' ; '),
        })
    }

    if (Array.isArray(article.otherAffiliations) && article.otherAffiliations.length > 0) {
        fields.push({
            field: 'otherAffiliations',
            text: article.otherAffiliations.join(' ; '),
        })
    }

    return fields
}

function createStructuredKeywordMatches(
    article: PubMedArticle,
    families: PubMedKeywordFamily[]
): MatchedKeywordFamily[] {
    const articleFields = buildArticleMatchFields(article)
    const structuredMatches: MatchedKeywordFamily[] = []

    families.forEach((family) => {
        const matchedTerms = new Set<string>()
        const matchedFields = new Set<PubMedMatchField>()

        articleFields.forEach(({ field, text }) => {
            family.patterns.forEach((pattern) => {
                if (!(pattern instanceof RegExp)) {
                    console.warn(`[PubMed] Ignoring invalid regex pattern in keyword family "${family.id}".`, pattern)
                    return
                }

                const patternMatches = getRegexMatches(pattern, text)

                if (patternMatches.length > 0) {
                    matchedFields.add(field)
                    patternMatches.forEach((match) => matchedTerms.add(match))
                }
            })
        })

        if (matchedTerms.size === 0) {
            return
        }

        structuredMatches.push({
            familyId: family.id,
            familyLabel: family.label,
            category: family.category,

            measureId: family.measureId,
            measureLabel: family.measureLabel,

            thresholdId: family.thresholdId,
            thresholdLabel: family.thresholdLabel,

            matchedTerms: Array.from(matchedTerms),
            matchedFields: Array.from(matchedFields),
        })
    })

    return structuredMatches
}

function deriveKeywordHits(matches: MatchedKeywordFamily[]): KeywordHits {
    const keywordHits: KeywordHits = {}

    const matchesByCategory = matches.reduce(
        (acc, match) => {
            if (!acc[match.category]) {
                acc[match.category] = []
            }

            acc[match.category]!.push(match)
            return acc
        },
        {} as Partial<Record<KeywordCategoryKey, MatchedKeywordFamily[]>>
    )

    ;(Object.keys(matchesByCategory) as KeywordCategoryKey[]).forEach((category) => {
        const categoryMatches = matchesByCategory[category] || []

        const labels = Array.from(
            new Set(categoryMatches.map((match) => match.thresholdLabel || match.measureLabel || match.familyLabel))
        ).slice(0, CUSTOM_MATCH_CAP_PER_CATEGORY)

        if (labels.length > 0) {
            keywordHits[category] = {
                hit: true,
                matches: labels,
            }
        }
    })

    return keywordHits
}

/**
 * Find which phrases appear in text (simple substring match).
 */
// Removing as it is no longer needed
function findMatches(text: string, phrases: string[]): string[] {
    const t = norm(text)
    const hits: string[] = []

    for (const p of phrases) {
        const np = norm(p)
        if (!np) continue
        if (t.includes(np)) hits.push(p)
    }

    return Array.from(new Set(hits))
}

/**
 * Title and abstract are the most important for screening ... a person will typically screen titles first and then shift to the abstract.
 */
function buildArticleText(article: PubMedArticle): string {
    const parts: string[] = []
    if (article.title) parts.push(article.title)
    if (article.abstract) parts.push(article.abstract)
    if (Array.isArray(article.industryAffiliations) && article.industryAffiliations.length > 0) {
        parts.push(article.industryAffiliations.join(' ; '))
    }
    if (Array.isArray(article.otherAffiliations) && article.otherAffiliations.length > 0) {
        parts.push(article.otherAffiliations.join(' ; '))
    }
    return parts.join('\n')
}

// -----------------------------
// Score #1: user's search terms
// -----------------------------

// A small stopword list to reduce noise
const STOPWORDS = new Set(['and', 'or'])

/**
 * Extract search phrases from the user's inputs.
 * - phrases: keep comma-separated chunks and multi-word phrases (strong matches)
 * - tokens: split into individual words (weaker matches)
 */
export function extractSearchTerms(params: PubMedSearchParams): { phrases: string[] } {
    const fields = [params.diseaseArea, params.interventions, params.sponsor, params.outcomes, params.authors]
        .filter(Boolean)
        .map(String)

    // We treat comma-separated chunks as the "units" to score.
    // This preserves phrases like "atopic dermatitis" as ONE concept.
    const raw = fields.join(' , ')

    const phrases = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

    return { phrases: Array.from(new Set(phrases)) }
}

/**
 * - searchTermsScore:
 *     Driven entirely by the user's search input.
 *     Used as the DEFAULT sort order.
 *
 * - customKeywordsScore:
 *     Driven by predefined outcome/HEOR keyword buckets
 *     (economic, treatment patterns, clinical effectiveness, etc.).
 *     Used as an alternate prioritization mode.
 *
 * These scores are intentionally kept separate so users can
 * switch perspectives without changing the underlying data.
 *
 * Each search phrase can contribute to the score AT MOST ONCE per article.
 *
 * Scoring rules per phrase:
 *   - Phrase appears in TITLE     → +6 points
 *   - Phrase appears in ABSTRACT  → +3 points
 *   - Phrase appears in BOTH      → +6 points (title wins)
 *
 * We intentionally do NOT:
 *   - Add abstract points if the phrase is already in the title
 *   - Count multiple occurrences of the same phrase
 *
 * Rationale:
 *   - Presence matters more than frequency
 *   - Prevents runaway scores from repetitive abstracts
 *   - Keeps scores comparable across articles
 */

export function scoreBySearchTerms(article: PubMedArticle, terms: { phrases: string[] }): number {
    const title = (article.title || '').toLowerCase()
    const abs = (article.abstract || '').toLowerCase()

    let score = 0

    for (const phrase of terms.phrases) {
        const p = phrase.toLowerCase().trim()
        if (!p) continue

        // Title wins. If it's in title, we do NOT also count abstract.
        if (title.includes(p)) {
            score += 6
            continue
        }

        // Only count abstract if NOT in title
        if (abs.includes(p)) {
            score += 3
        }
    }

    return score
}

// -----------------------------
// Score #2: custom keyword buckets (Updating this so that the keywords use regex and have both core keywords and disease-specific keywords)
// The change is necessary so that we aren't repeating the same outcomes over and over just because they have a different variation in the label (healthcare resource use doesnt need to appear different from hcru)
// -----------------------------
export function classifyByCustomKeywords(
    article: PubMedArticle,
    activeFamilies: PubMedKeywordFamily[]
): {
    matchedKeywordFamilies: MatchedKeywordFamily[]
    keywordHits: KeywordHits
    score: number
} {
    const matchedKeywordFamilies = createStructuredKeywordMatches(article, activeFamilies)

    const keywordHits = deriveKeywordHits(matchedKeywordFamilies)

    const matchesByCategory = matchedKeywordFamilies.reduce(
        (acc, match) => {
            if (!acc[match.category]) {
                acc[match.category] = []
            }

            acc[match.category]!.push(match)
            return acc
        },
        {} as Partial<Record<KeywordCategoryKey, MatchedKeywordFamily[]>>
    )

    let score = 0
    let categoriesHit = 0

    ;(Object.keys(matchesByCategory) as KeywordCategoryKey[]).forEach((category) => {
        const categoryMatches = matchesByCategory[category] || []

        /**
         * Preserve the existing score behavior:
         *
         * Each uniquely configured keyword family contributes once, up to
         * the existing per-category cap.
         *
         * Parent and threshold families remain separate configured
         * families, so both may contribute when both are detected.
         */
        const uniqueFamilyIds = Array.from(new Set(categoryMatches.map((match) => match.familyId))).slice(
            0,
            CUSTOM_MATCH_CAP_PER_CATEGORY
        )

        if (uniqueFamilyIds.length > 0) {
            categoriesHit += 1
            score += uniqueFamilyIds.length * (CUSTOM_WEIGHTS[category] || 1)
        }
    })

    score += breadthBonus(categoriesHit)

    return {
        matchedKeywordFamilies,
        keywordHits,
        score,
    }
}

// -----------------------------
// Main enrichment entrypoint
// -----------------------------

function extractInterventionTerms(params: PubMedSearchParams): string[] {
    if (!params.interventions) return []
    return params.interventions
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
}

function extractOutcomeTerms(params: PubMedSearchParams): string[] {
    if (!params?.outcomes) return []

    return params.outcomes
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean)
}

function getGeographyArticleFields(article: PubMedArticle): Array<{ field: PubMedGeographyTextField; text: string }> {
    const fields: Array<{
        field: PubMedGeographyTextField
        text: string
    }> = []

    if (article.title) {
        fields.push({
            field: 'title',
            text: article.title,
        })
    }

    if (article.abstract) {
        fields.push({
            field: 'abstract',
            text: article.abstract,
        })
    }

    return fields
}

function getFirstPatternMatch(patterns: RegExp[], text: string): string | undefined {
    for (const pattern of patterns) {
        const matches = getRegexMatches(pattern, text)

        if (matches.length > 0) {
            return matches[0]
        }
    }

    return undefined
}

function createGeographyMatchKey(match: PubMedGeographyMatch): string {
    return [
        match.geographyId,
        match.matchedField,
        match.matchType,
        match.matchedSourceId || '',
        match.matchedTerm.toLowerCase(),
    ].join('|')
}

function enrichArticleGeography(article: PubMedArticle, indicationId?: string): PubMedGeographyEnrichment {
    const articleFields = getGeographyArticleFields(article)
    const geographyMatches: PubMedGeographyMatch[] = []
    const seenMatchKeys = new Set<string>()

    const matchedSourceIds = new Set<string>()
    const matchedSourceLabels = new Set<string>()

    const addMatch = (match: PubMedGeographyMatch): void => {
        const key = createGeographyMatchKey(match)

        if (seenMatchKeys.has(key)) {
            return
        }

        seenMatchKeys.add(key)
        geographyMatches.push(match)
    }

    /*
     * 1. Explicit country/geography names and guarded demonyms.
     *
     * Examples:
     * - "Germany"
     * - "United Kingdom"
     * - "Japanese patients"
     *
     * Only title and abstract are evaluated. Author affiliations are
     * intentionally excluded because they do not reliably establish
     * study geography.
     */
    PUBMED_GEOGRAPHY_ENTITIES.filter((entity) => entity.enabled !== false).forEach((entity) => {
        const explicitPatterns = getExplicitGeographyPatterns(entity)

        const demonymPatterns = getContextualDemonymPatterns(entity)

        articleFields.forEach(({ field, text }) => {
            const explicitTerm = getFirstPatternMatch(explicitPatterns, text)

            if (explicitTerm) {
                addMatch({
                    geographyId: entity.id,
                    geographyLabel: entity.label,
                    geographyLevel: entity.level,
                    matchedField: field,
                    matchedTerm: explicitTerm,
                    matchType: 'explicitGeography',
                    confidence: PUBMED_GEOGRAPHY_FIELD_CONFIDENCE.explicitGeography[field],
                })
            }

            const demonymTerm = getFirstPatternMatch(demonymPatterns, text)

            if (demonymTerm) {
                addMatch({
                    geographyId: entity.id,
                    geographyLabel: entity.label,
                    geographyLevel: entity.level,
                    matchedField: field,
                    matchedTerm: demonymTerm,
                    matchType: 'explicitGeography',
                    confidence: PUBMED_GEOGRAPHY_FIELD_CONFIDENCE.explicitGeography[field],
                })
            }
        })
    })

    /*
     * 2. Named claims databases, EHRs, registries, cohorts, and surveys.
     *
     * Every matched source is retained, but geography is inferred only
     * when inference.mode is "direct".
     *
     * Contextual and multinational sources are recorded without assigning
     * every country associated with the source.
     */
    const activeSources = getActivePubMedGeographySources(indicationId as GeographyIndicationId | undefined)

    activeSources.forEach((source) => {
        const sourcePatterns = getGeographySourcePatterns(source)

        articleFields.forEach(({ field, text }) => {
            const matchedTerm = getFirstPatternMatch(sourcePatterns, text)

            if (!matchedTerm) {
                return
            }

            matchedSourceIds.add(source.id)
            matchedSourceLabels.add(source.label)

            if (source.inference.mode !== 'direct') {
                return
            }

            source.inference.geographyIds.forEach((geographyId) => {
                const entity = PUBMED_GEOGRAPHY_BY_ID.get(geographyId)

                if (!entity) {
                    return
                }

                addMatch({
                    geographyId: entity.id,
                    geographyLabel: entity.label,
                    geographyLevel: entity.level,
                    matchedField: field,
                    matchedTerm,
                    matchType: 'dataSource',
                    matchedSourceId: source.id,
                    matchedSourceLabel: source.label,
                    confidence: source.inference.confidence,
                })
            })
        })
    })

    const studyGeographyIds = Array.from(new Set(geographyMatches.map((match) => match.geographyId)))

    const studyGeographyLabels = studyGeographyIds
        .map((geographyId) => PUBMED_GEOGRAPHY_BY_ID.get(geographyId)?.label)
        .filter((label): label is string => Boolean(label))

    const { geographyRollupIds, regionRollupIds } = buildGeographyRollups(studyGeographyIds)

    return {
        studyGeographyIds,
        studyGeographyLabels,
        geographyMatches,
        matchedSourceIds: Array.from(matchedSourceIds),
        matchedSourceLabels: Array.from(matchedSourceLabels),
        geographyRollupIds,
        regionRollupIds,
    }
}

export function enrichArticles(articles: PubMedArticle[], params: PubMedSearchParams): EnrichedPubMedArticle[] {
    const terms = extractSearchTerms(params)
    const interventionTerms = extractInterventionTerms(params)
    const activeFamilies = getActiveKeywordFamilies(params)
    const indicationId = inferIndicationIdFromParams(params)

    return (articles || []).map((a, idx) => {
        const titleAbstractText = `${a.title || ''}\n${a.abstract || ''}`

        const {
            matchedKeywordFamilies,
            keywordHits,
            score: customKeywordsScore,
        } = classifyByCustomKeywords(a, activeFamilies)

        const geography = enrichArticleGeography(a, indicationId)

        const searchTermsScore = scoreBySearchTerms(a, terms)

        const interventionMatches = findMatches(titleAbstractText, interventionTerms)

        const outcomeMatches = Array.from(
            new Set(
                matchedKeywordFamilies.map((match) => match.thresholdLabel || match.measureLabel || match.familyLabel)
            )
        )

        return {
            ...a,
            ...geography,
            matchedKeywordFamilies,
            keywordHits,
            searchTermsScore,
            customKeywordsScore,
            interventionMatches,
            outcomeMatches,
            _pubmedRank: idx,
        }
    })
}

/**
 * Sort enriched articles by either score, with PubMed rank as tie-breaker but we are now prioritizing those with affiliations higher.
 * Default mode should be 'searchTerms' now that we have built that out to be more usable with the pre-loaded search terms.
 * Previously I was forcing the keyword score because I wanted to prioritize articles that had those keywords even if someone didnt search for them
 * This will make the tool more flexible
 */

// TODO Once we include sorting/filtering then consider alternative approaches to industry affiliations
function hasIndustryAffiliation(article: EnrichedPubMedArticle): boolean {
    return Array.isArray(article.industryAffiliations) && article.industryAffiliations.length > 0
}

function getDefaultArticlePriorityScore(article: EnrichedPubMedArticle): number {
    return (
        (hasIndustryAffiliation(article) ? 1000 : 0) +
        (article.searchTermsScore || 0) * 10 +
        (article.customKeywordsScore || 0)
    )
}

/**
 * Updated the process for sorting articles.
 * Default mode now prioritizes:
 * industry affiliation (pharma companies) → search string score → keyword score → recency (more recent articles at top)
 */
export function sortEnrichedArticles(enriched: EnrichedPubMedArticle[], mode: SortMode): EnrichedPubMedArticle[] {
    const arr = [...(enriched || [])]

    arr.sort((a, b) => {
        const primary =
            mode === 'customKeywords'
                ? b.customKeywordsScore - a.customKeywordsScore
                : getDefaultArticlePriorityScore(b) - getDefaultArticlePriorityScore(a)

        if (primary !== 0) return primary

        const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0)
        if (yearDiff !== 0) return yearDiff

        return a._pubmedRank - b._pubmedRank
    })

    return arr
}
